'use client';

import Link from 'next/link';
import { ArrowRight, Bot, CalendarClock, Clock, Megaphone, Sparkles, Table2 } from 'lucide-react';
import AIScansShell from '@/components/ai-scans/AIScansShell';
import { useScreenerMeta } from '@/components/ai-scans/useScreenerMeta';
import { formatCount, formatDateTime, timeAgo } from '@/components/ai-scans/format';

const ENTRY_CARDS = [
  {
    href: '/ai-scans/screener',
    title: 'Stock Screener',
    description:
      'Sort and filter every listed company by price moves, valuation, growth, debt and who owns it. Start from a ready-made scan if you are new.',
    cta: 'Open the screener',
    icon: Table2,
  },
  {
    href: '/ai-scans/announcements',
    title: 'Announcements',
    description:
      'What companies told the stock exchange today — results, board meetings, dividends, orders — with the original PDF one click away.',
    cta: 'Read announcements',
    icon: Megaphone,
  },
  {
    href: '/ai-scans/claude',
    title: 'Use with Claude',
    description:
      'Copy one prompt into Claude and ask questions in plain English, like "find profitable small companies with little debt".',
    cta: 'Set up in 1 minute',
    icon: Bot,
  },
];

export default function AIScansPage() {
  const { meta, error } = useScreenerMeta();
  const marketUpdated = meta?.last_synced.market;
  // Rounded down ("6,700+") and only once the table is filled, so the hero never reads "0+".
  const stockCountLabel =
    meta && meta.total_stocks >= 1000 ? `${formatCount(Math.floor(meta.total_stocks / 100) * 100)}+` : '6,700+';
  const fundamentalsUpdated = meta?.last_synced.fundamentals;

  return (
    <AIScansShell>
      <section className="relative overflow-hidden rounded-3xl border border-zinc-200 bg-white px-6 py-10 md:px-12 md:py-14 dark:border-zinc-800 dark:bg-zinc-900">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
          <Sparkles className="h-3.5 w-3.5" />
          Free · no sign-up needed
        </span>
        <h1 className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl dark:text-white">
          Scan {stockCountLabel} Indian stocks
          and ask Claude about them
        </h1>
        <p className="mt-4 max-w-2xl text-base text-zinc-600 md:text-lg dark:text-zinc-400">
          Find companies that match what you care about — growing sales, low debt, strong momentum —
          and read their latest announcements. Every number comes with a plain-English explanation.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            href="/ai-scans/screener"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-sm shadow-emerald-600/20 transition-colors hover:bg-emerald-500"
          >
            Start scanning
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            href="/ai-scans/claude"
            className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 transition-colors hover:border-emerald-500/50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
          >
            <Bot className="h-4 w-4" />
            Ask Claude
          </Link>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        {ENTRY_CARDS.map(({ href, title, description, cta, icon: Icon }) => (
          <Link
            key={href}
            href={href}
            className="group flex flex-col rounded-2xl border border-zinc-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-500/[0.08] dark:border-zinc-800 dark:bg-zinc-900"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 transition-colors group-hover:bg-emerald-500 group-hover:text-white dark:text-emerald-400">
              <Icon className="h-6 w-6" />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">{title}</h2>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">{description}</p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
              {cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        ))}
      </section>

      <section className="mt-8 rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">
        <h2 className="text-base font-semibold text-zinc-900 dark:text-white">How it works</h2>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <div className="flex gap-3">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              <strong className="font-semibold text-zinc-900 dark:text-zinc-100">Prices, returns and indicators</strong>{' '}
              refresh every hour while the market is open.
            </p>
          </div>
          <div className="flex gap-3">
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              <strong className="font-semibold text-zinc-900 dark:text-zinc-100">Financials, shareholding and scores</strong>{' '}
              refresh once a day, after companies file their results.
            </p>
          </div>
        </div>
        <p className="mt-5 border-t border-zinc-100 pt-4 text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-500">
          {meta ? (
            <>
              Last updated: prices{' '}
              <span title={formatDateTime(marketUpdated) ?? undefined} className="font-medium text-zinc-700 dark:text-zinc-300">
                {timeAgo(marketUpdated) ?? 'not yet'}
              </span>
              , financials{' '}
              <span title={formatDateTime(fundamentalsUpdated) ?? undefined} className="font-medium text-zinc-700 dark:text-zinc-300">
                {timeAgo(fundamentalsUpdated) ?? 'not yet'}
              </span>
              .
            </>
          ) : error ? (
            'Could not check when the data was last updated.'
          ) : (
            <span className="inline-block h-3 w-64 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
          )}
        </p>
      </section>
    </AIScansShell>
  );
}
